import { inviteIdFromLink, isProfileComplete } from '@echo/core';
import { useExerciseMinutesSync, useGlobalGroup, useMe, useSession } from '@echo/core/react';
import {
  createFileRoute,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { AppShell } from '@/components/app-shell';
import { OfflineGate } from '@/components/offline-gate';
import { ReminderSync } from '@/components/reminder-sync';
import { holdInvite, releaseHeldInvite, useHeldInvite } from '@/lib/held-invite';
import { useWelcomeSeen } from '@/lib/welcome-store';

export const Route = createFileRoute('/_app')({ component: AppLayout });

/**
 * Sign-in → complete your profile → welcome → app: nothing under the shell renders without a session, a
 * complete OnEarly profile (docs/mvp/02-ONBOARDING.md) and Brian's welcome seen once for
 * this sign-in. An invitation link opened before then is held on the way through and opened
 * once the person is in.
 */
function AppLayout() {
  const session = useSession();
  const me = useMe();
  const welcomeSeen = useWelcomeSeen();
  const pathname = useLocation({ select: (location) => location.pathname });
  // Everyone belongs to the global Stronger Memory group; joins (idempotently) once signed in.
  useGlobalGroup();
  useExerciseMinutesSync();
  const signedIn = session.status === 'authenticated';
  const needsProfile = signedIn && me.data !== undefined && !isProfileComplete(me.data);
  const settled = session.status !== 'unknown' && (!signedIn || me.isFetched);
  const inside = settled && signedIn && !needsProfile && welcomeSeen;
  // Join group is a signed-in page: the redirects below would drop the link, so hold it first.
  const linkedInvite = inviteIdFromLink(pathname);
  useEffect(() => {
    if (settled && !inside && linkedInvite) holdInvite(linkedInvite);
  }, [settled, inside, linkedInvite]);

  if (session.status === 'unknown') return null;
  if (!signedIn) return <Navigate to="/sign-in" replace />;
  // `isFetched` covers a failed fetch too: an unreachable Core must not blank the app.
  if (!me.isFetched) return null;
  if (needsProfile) return <Navigate to="/onboarding" replace />;
  if (!welcomeSeen) return <Navigate to="/welcome" replace />;
  return (
    <>
      <HeldInviteOpener />
      <ReminderSync />
      <OfflineGate />
      <AppShell>
        <Outlet />
      </AppShell>
    </>
  );
}

/** Opens Join group for an invitation link held on the way in, once the app is open to them. */
function HeldInviteOpener() {
  const navigate = useNavigate();
  const inviteId = useHeldInvite();
  useEffect(() => {
    if (!inviteId) return;
    releaseHeldInvite();
    // In place of the Home the way in ended on: Not now goes back there.
    void navigate({ to: '/invite/$inviteId', params: { inviteId }, replace: true });
  }, [inviteId, navigate]);
  return null;
}
